import {useQueryClient} from "@tanstack/react-query";
import {eachMonthOfInterval, formatISO, startOfMonth} from "date-fns";
import {useMainLayout} from "../../../layouts/Main";
import {monthRange, monthVerticalDayRange} from "../../../components/Planner/utils";

/*
 * This hook provides a function to get the prices in the Query client cache
 * given 2 dates.
 * Since in the cache the prices are stored by month, we need to get the prices
 * for each month and merge them.
 */
const useCachedPrices = () => {
    const queryClient = useQueryClient();
    const {isMobile} = useMainLayout();

    return (apartmentId, rateId, dateStart, dateEnd) => {
        const months = eachMonthOfInterval({start: dateStart, end: dateEnd})
        const numberOfMonths = months.length

        const result = {
            timestamps: [],
            pricesCent: {},  // key is the number of people, value is an array of prices cent
            enabled: [],
            minNights: [],
            minPeople: [],
            checkin: [],
            checkout: [],
        }

        for (let i = 0; i < numberOfMonths; i++) {
            const monthStart = startOfMonth(months[i])
            const month = monthStart.getMonth()
            const year = monthStart.getFullYear()

            // I'm assuming that the mobile use the monthVertical view and the desktop the timeline
            // The month vertical view use different dates for the first and last day of the month.
            const [start, end] = isMobile ? monthVerticalDayRange(month, year) : monthRange(month, year);

            const queryKey = [
                'apartmentPrices',
                apartmentId,
                formatISO(start, {representation: "date"}),
                formatISO(end, {representation: "date"})
            ]

            // Get data into query cache
            const data = queryClient.getQueryData(queryKey)
            const rate = data?.find(r => r.rateId.toString() === rateId.toString())

            if (data && rate) {
                let startIndex = rate.timestamps.findIndex(t => t >= formatISO(dateStart, {representation: "date"}))
                let endIndex = rate.timestamps.findIndex(t => t >  formatISO(dateEnd, {representation: "date"}));
                endIndex = endIndex === -1 ? rate.timestamps.length : endIndex;

                if (rate) {
                    result.timestamps.push(...rate.timestamps.slice(startIndex, endIndex))
                    result.enabled.push(...rate.enabled.slice(startIndex, endIndex))
                    result.minNights.push(...rate.minNights.slice(startIndex, endIndex))
                    result.minPeople.push(...rate.minPeople.slice(startIndex, endIndex))
                    result.checkin.push(...rate.checkin.slice(startIndex, endIndex))
                    result.checkout.push(...rate.checkout.slice(startIndex, endIndex))

                    rate.prices.forEach(pr => {
                        if (!result.pricesCent[pr.people]) {
                            result.pricesCent[pr.people] = []
                        }
                        result.pricesCent[pr.people].push(...pr.pricesCent.slice(startIndex, endIndex))
                    })
                }
            }
        }

        return result;
    }
}

export default useCachedPrices;